import { useState, useEffect } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useAuth } from "@/context/AuthContext";
import { Bell, ClipboardList, FileCheck, MessageSquare, Info } from "lucide-react";
import { apiFetch } from "@/service/api";
import { Modal } from "@/components/Usados/modal";

interface Notification {
  id: number;
  titulo: string;
  mensaje: string;
  estado_notificacion: number;
  visto: boolean;
  fecha_envio: string;
}

export default function NotificationDropdown() {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isAllOpen, setIsAllOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = async () => {
    if (!user?.id || !token) return;
    try {
      const response = await apiFetch(`notificaciones/find-by-user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Opcional: Polling cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id, token]);

  const unreadCount = notifications.filter((n) => !n.visto).length;

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      const response = await apiFetch(`notificaciones/mark-as-read/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, visto: true } : n))
        );
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      const response = await apiFetch(`notificaciones/mark-all-as-read/${user.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, visto: true })));
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const openNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    setIsAllOpen(false);
    closeDropdown();
    if (!notification.visto) handleMarkAsRead(notification.id);
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "ahora mismo";
    if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} h`;
    return `hace ${Math.floor(diffInSeconds / 86400)} d`;
  };

  const getIcon = (titulo: string) => {
    const t = titulo.toLowerCase();
    if (t.includes('tarea')) return <ClipboardList className="w-5 h-5 text-blue-500" />;
    if (t.includes('encuesta')) return <FileCheck className="w-5 h-5 text-purple-500" />;
    if (t.includes('mensaje')) return <MessageSquare className="w-5 h-5 text-green-500" />;
    return <Info className="w-5 h-5 text-gray-400" />;
  };

  return (
    <div className="relative">
      <button
        className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        onClick={toggleDropdown}
      >
        {unreadCount > 0 && (
          <span className="absolute right-0 top-0.5 z-10 h-2 w-2 rounded-full bg-orange-400">
            <span className="absolute inline-flex w-full h-full bg-orange-400 rounded-full opacity-75 animate-ping"></span>
          </span>
        )}
        <Bell className="w-5 h-5" />
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute mt-[17px] flex h-[480px] w-[min(361px,calc(100vw-72px))] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px]"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Notificaciones
          </h5>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs font-medium text-brand-500 hover:underline"
            >
              Marcar todo como leido
            </button>
          )}
        </div>

        <ul className="flex flex-col h-auto overflow-y-auto custom-scrollbar flex-1">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-10 text-gray-500">
              <Bell className="w-10 h-10 mb-2 opacity-20" />
              <p className="text-sm">No tienes notificaciones</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => openNotification(notification)}
                  className={`flex w-full gap-3 rounded-lg border-b border-gray-100 p-3 px-4.5 py-3 text-left hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-white/5 ${!notification.visto ? "bg-brand-50/50 dark:bg-brand-500/5" : ""
                    }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(notification.titulo)}
                  </div>

                  <div className="flex-1">
                    <span className="block mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                      {notification.titulo}
                    </span>
                    <span className="block mb-1.5 text-theme-sm text-gray-500 dark:text-gray-400">
                      {notification.mensaje}
                    </span>
                    <span className="text-gray-400 text-theme-xs dark:text-gray-500">
                      {getRelativeTime(notification.fecha_envio)}
                    </span>
                  </div>
                  {!notification.visto && (
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                    </div>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>

        <button
          type="button"
          onClick={() => {
            closeDropdown();
            setIsAllOpen(true);
          }}
          className="block w-full px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          Ver todas las notificaciones
        </button>
      </Dropdown>

      <Modal
        isOpen={isAllOpen}
        onClose={() => setIsAllOpen(false)}
        className="max-w-md p-5 sm:p-6 max-h-[85vh]"
      >
        <div role="dialog" aria-modal="true" aria-labelledby="all-notifications-title">
          <h2 id="all-notifications-title" className="mb-4 pr-10 text-lg font-semibold text-gray-800 dark:text-gray-200">
            Todas las notificaciones
          </h2>
          {notifications.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No tienes notificaciones
            </p>
          ) : (
            <ul className="max-h-[65vh] space-y-2 overflow-y-auto">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => openNotification(notification)}
                    className="flex w-full gap-3 rounded-lg border border-gray-200 p-3 text-left hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                  >
                    <span className="mt-1 shrink-0">
                      {getIcon(notification.titulo)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-sm font-semibold text-gray-800 dark:text-white/90">
                        {notification.titulo}
                      </span>
                      <span className="mt-1 block break-words text-sm text-gray-500 dark:text-gray-400">
                        {notification.mensaje}
                      </span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {getRelativeTime(notification.fecha_envio)}
                      </span>
                    </span>
                    {!notification.visto && (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={selectedNotification !== null}
        onClose={() => setSelectedNotification(null)}
        className="max-w-md p-5 sm:p-6"
      >
        {selectedNotification && (
          <div role="dialog" aria-modal="true" aria-labelledby="notification-title">
            <div className="mb-4 flex items-center gap-3 pr-10">
              {getIcon(selectedNotification.titulo)}
              <h2 id="notification-title" className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {selectedNotification.titulo}
              </h2>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-600 dark:text-gray-300">
              {selectedNotification.mensaje}
            </p>
            <p className="mt-5 border-t border-gray-100 pt-3 text-xs text-gray-400 dark:border-gray-700">
              {new Date(selectedNotification.fecha_envio).toLocaleString()}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}